import type { EquipmentType } from '../equipment/constants.ts'
import { equipmentTypes } from '../equipment/equipment-types.ts'
import {
  vArray,
  vBoolean,
  vLiteral,
  vObject,
  type vOutput,
  vPicklist,
  vString,
  vVariant,
} from '../validation/index.ts'

export const accountUserPreferences = [
  'shipment-list-condensed',
  'ltl_quote_remember_destination',
  'ltl_quote_remember_origin',
  'ltl_quote_remember_product',
  'parcel_quote_remember_destination',
  'parcel_quote_remember_origin',
  'parcel_quote_remember_product',
  'spot-quote-disable-email-notification',
  'spot-quote-list-show-all',
  'truckload_quote_remember_destination',
  'truckload_quote_remember_origin',
  'truckload_quote_remember_equipment',
  'truckload_quote_remember_stops',
  'autoSubscribeNewVendors',
] as const

export const condensedColumns = [
  'tracking',
  'status',
  'carrier',
  'origin',
  'origin-company',
  'pickup',
  'destination',
  'destination-company',
  'delivery',
  'mode',
  'pickup-date-actual',
  'delivery-date-actual',
  'equipment-type',
  'direction',
  'quoted-by',
  'booked-by',
  'interline',
  'vendor',
  'archived',
  'trailer-number',
  'pickup-confirmation',
  'unit-count',
  'total-price',
  'shipper-num',
] as const
export type CondensedColumn = (typeof condensedColumns)[number]

export type ColumnPreference = {
  id: CondensedColumn
  visible: boolean
}

const shipmentListOptions = ['cards', 'condensed', 'calendar'] as const
export type ShipmentListPreference = (typeof shipmentListOptions)[number]

export const landingPageOptions = [
  'dashboard',
  'quote-page',
  'pending-shipments',
  'active-shipments',
  'analytics',
  'auditing',
] as const
export type LandingPagePreference = (typeof landingPageOptions)[number]

export type AccountUserPreferences = {
  'spot-quote-disable-email-notification'?: boolean
  'spot-quote-list-show-all'?: boolean
  ltl_quote_remember_destination?: boolean
  ltl_quote_remember_origin?: boolean
  ltl_quote_remember_product?: boolean
  parcel_quote_remember_destination?: boolean
  parcel_quote_remember_origin?: boolean
  parcel_quote_remember_product?: boolean
  truckload_quote_remember_destination?: boolean
  truckload_quote_remember_origin?: boolean
  truckload_quote_remember_equipment?: boolean
  truckload_quote_remember_stops?: boolean
  tlEquipmentTypes?: EquipmentType[]
  condensedColumns?: ColumnPreference[]
  subscribedVendors?: string[]
  autoSubscribeNewVendors?: boolean
  shipmentList?: ShipmentListPreference
  landingPage?: LandingPagePreference
}

// Valibot schema for column preferences
const columnPreferenceSchema = vObject({
  id: vPicklist(condensedColumns),
  visible: vBoolean(),
})

// Valibot schema for updating user preferences (discriminated union)
export const updateUserPreferenceSchema = vVariant('type', [
  vObject({
    type: vLiteral('equipment-types'),
    value: vArray(vPicklist(equipmentTypes.map(t => t.key))),
  }),
  vObject({
    type: vLiteral('condensed-columns'),
    value: vArray(columnPreferenceSchema),
  }),
  vObject({
    type: vLiteral('subscribed-vendors'),
    value: vArray(vString()),
  }),
  vObject({
    type: vLiteral('shipment-list'),
    value: vPicklist(shipmentListOptions),
  }),
  vObject({
    type: vLiteral('landing-page'),
    value: vPicklist(landingPageOptions),
  }),
])

export type UpdateUserPreferenceDTO = vOutput<typeof updateUserPreferenceSchema>

export const toggleUserPreferenceSchema = vObject({
  value: vBoolean(),
  feature: vPicklist(accountUserPreferences),
})

export type ToggleUserPreferenceDTO = vOutput<typeof toggleUserPreferenceSchema>
